"use client";
import Link from "next/link";
import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { getStrapiURL } from "@aicodepro/web-builder-utils";

interface Feature {
  id: string;
  attributes: {
    name: string;
  };
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  pricePeriod: string;
  isRecommended: boolean;
  product_features: {
    data: Feature[];
  };
}

interface PriceProps {
  data: {
    id: string;
    title: string;
    plans: Plan[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function Modal({ isOpen, onClose }: ModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const router = useRouter();

  const token = process.env.AICODEPRO_FORM_SUBMISSION_TOKEN;

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formData = {
      data: {
        name,
        email,
        mobile,
      },
    };

    // console.log(formData);
    try {
      const response = await fetch(
        getStrapiURL() + "/api/web-builder/contact-form-submissions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        router.push("/aicodepro-appointment");
      } else {
        console.error("Form Submission Error");
      }
    } catch (error) {
      console.error("Error submitting form: ", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative bg-white p-8 rounded-lg shadow-lg max-w-lg w-full">
        <h2 className="text-3xl text-th-bg-col font-bold mb-8">Custom Plan</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Enter your Name*"
              className="w-full px-3 py-2 border rounded"
              required
              onChange={(e) => setName(e.target.value)}
              value={name}
            />
          </div>
          <div className="mb-4">
            <input
              type="email"
              placeholder="Enter your Email*"
              className="w-full px-3 py-2 border rounded"
              required
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />
          </div>
          <div className="mb-4">
            <input
              type="tel"
              placeholder="Enter your Mobile No.*"
              className="w-full px-3 py-2 border rounded"
              required
              onChange={(e) => setMobile(e.target.value)}
              value={mobile}
            />
          </div>
          <button
            type="submit"
            className="mt-4 px-6 py-2 bg-th-btncol-1 text-black rounded hover:bg-th-btncol-2"
          >
            Schedule Your Appointment
          </button>
        </form>
        <button
          onClick={onClose}
          className="absolute mt-4 text-black text-xl px-4 py-2 rounded-lg top-[-10px] right-[10px] transform transition-transform duration-200 hover:scale-110"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function Pricing({ data }: PriceProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    data?.isLive && (
      <>
        <div className="relative w-full overflow-hidden bg-gradient-to-b from-th-bg-col to-th-bg-col-rich">
          <div className="absolute top-0 left-0 w-[200%] h-24 bg-gradient-to-b from-th-bg-col to-th-bg-col-rich z-10 transform-rotate--2 clip-path-wave"></div>
        </div>

        <div className="relative w-full h-[85px] bg-th-bg-col z-10">
          <div className="absolute lg:top-0 top-[-2px] left-0 right-0 lg:h-[83px] h-20 bg-[url('https://oextech.blr1.digitaloceanspaces.com/Website%5Boex%5D/brandLogo/3.png')] bg-cover transform rotate-180"></div>
        </div>

        <section className="pb-20 bg-th-bg-col pt-4">
          <div className="container px-4 mx-auto">
            <div className="max-w-2xl mx-auto mb-16 text-center">
              {
                data?.useH1Tag ? (
                  <h1 className="text-3xl text-th-title-col font-bold lg:text-4xl md:text-4xl">
                {data?.title?.split(" ").map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line == "Profitable" ? (
                      <span className="relative whitespace-nowrap text-orange-400">
                        <Image
                          src="https://oextech.blr1.digitaloceanspaces.com/AiCodePro/line1.svg"
                          alt="Highlight Background"
                          width={100}
                          height={100}
                          className="absolute top-[-64%] left-[2px] h-[150px] w-full"
                        />
                        <span className="relative">{line}</span>
                      </span>
                    ) : (
                      <span className="leading-snug">{line}</span>
                    )}
                    {index !== array.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h1>
                ) : (
                  <h2 className="text-3xl text-th-title-col font-bold lg:text-4xl md:text-4xl">
                {data?.title?.split(" ").map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line == "Profitable" ? (
                      <span className="relative whitespace-nowrap text-orange-400">
                        <Image
                          src="https://oextech.blr1.digitaloceanspaces.com/AiCodePro/line1.svg"
                          alt="Highlight Background"
                          width={100}
                          height={100}
                          className="absolute top-[-64%] left-[2px] h-[150px] w-full"
                        />
                        <span className="relative">{line}</span>
                      </span>
                    ) : (
                      <span className="leading-snug">{line}</span>
                    )}
                    {index !== array.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h2>
                )
              }
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 md:grid-cols-2 justify-items-center mx-auto">
              {data?.plans.map((plan: Plan) => (
                <div key={plan?.id} className="w-full p-4 mb-8 flex">
                  <div className="pricing-card flex flex-col p-6 space-y-6  transition-transform duration-300 ease-in-out hover:scale-105 hover:-translate-y-2 rounded-lg bg-white overflow-hidden cursor-pointer w-full relative">
                    <div className="corner-expand"></div>
                    {plan?.isRecommended && (
                      <div className="absolute lg:right-[-5px] md:right-[-5px] right-[-4.1px] lg:top-[-29px] md:top-[-28px] top-[-28.1px] z-20 h-[100px] w-[100px] overflow-hidden text-right">
                        <span className="ribbon">{"Popular"}</span>
                      </div>
                    )}
                    {plan?.name === "FREE" && (
                      <div className="absolute lg:right-[-5px] md:right-[-5px] right-[-4.1px] lg:top-[-29px] md:top-[-28px] top-[-28.1px] z-20 h-[100px] w-[100px] overflow-hidden text-right">
                        <span className="ribbon2">{"Free"}</span>
                      </div>
                    )}
                    <div className="relative z-20 text-left">
                      <h2 className="text-xl font-bold mb-4 text-th-title-col-2">
                        {plan?.name}
                      </h2>
                      <span className="text-3xl font-bold text-th-subtitle-col-2">
                        <span className="text-xl font-bold text-th-subtitle-col-2">
                          ₹{" "}
                        </span>
                        {plan?.name === "CUSTOM" ? "..." : plan.price}
                        {plan?.name !== "FREE" && (
                          <span className="text-sm font-bold text-th-subtitle-col-2">
                            + GST
                          </span>
                        )}
                        <span className="ml-1 text-sm tracking-wider text-th-subtitle-col-2">
                          /{plan?.pricePeriod?.toLowerCase()}
                        </span>
                      </span>
                    </div>
                    <ul className="flex-1 mb-6 text-th-textcol-3 font-semibold">
                      {plan?.product_features?.data.map((feature: Feature) => (
                        <li key={feature.id} className="flex mb-2 space-x-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="flex-shrink-0 w-6 h-6 text-th-linkcol-2 text-black"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                          <span className="text-sm">
                            {feature?.attributes?.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {plan.name === "CUSTOM" ? (
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-block px-5 py-3 font-semibold tracking-wider text-center rounded-3xl bg-th-btncol-1 text-th-btntext-2 hover:bg-th-btncol-2"
                      >
                        Get Started
                      </button>
                    ) : (
                      <Link
                        href={plan.description || ""}
                        target="_blank"
                        className="inline-block px-5 py-3 font-semibold tracking-wider text-center rounded-3xl bg-th-btncol-1 text-th-btntext-2 hover:bg-th-btncol-2"
                      >
                        {plan.name === "FREE" ? "Free Start" : "Get Started"}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    )
  );
}
